import { forwardRef, useRef } from 'react';

import { Box, HStack, Text, VStack, theme } from '@squire/ui';

import type { BottomSheetRef } from '@/components/_shared/BottomSheet';
import { BottomSheet } from '@/components/_shared/BottomSheet';
import { ScreenHeader } from '@/components/_shared/ScreenHeader';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import composeRefs from '@/utils/composeRefs';
import { formatPrice } from '@/utils/internationalizationUtils';

import { styles } from './styles';
import type { ConfirmBookingTaxesAndFeesBottomSheetProps } from './types';

const ConfirmBookingTaxesAndFeesBottomSheet = forwardRef<
  BottomSheetRef,
  ConfirmBookingTaxesAndFeesBottomSheetProps
>(
  (
    {
      breakdown,
      brandSettings,
      currency = 'USD',
      isWaitlistMode = false,
      feeApplies = true,
    },
    ref,
  ) => {
    const { t, i18n } = useScreenTranslations('ConfirmBookingBottomSheet');

    const taxesBottomSheetRef = useRef<BottomSheetRef>(null);

    const feesAmount = breakdown?.totalFee || 0;
    const waitlistFeeAmount = breakdown?.waitingListFee || 0;
    const taxesAmount = breakdown?.taxes.reduce(
      (acc, tax) => acc + tax.amount,
      0,
    );

    const getFeeDescription = () => {
      if (
        brandSettings?.appointmentFeeEnabled &&
        brandSettings.appointmentFeePaidBy === 'client'
      ) {
        return t('feesDescriptionWithBarbershop');
      }
      return t('feesDescription');
    };

    return (
      <BottomSheet
        accessibilityLabel={t('taxes')}
        scrollable={false}
        style={styles.bottomSheet}
        snapPoints={[{ percentage: '30%' }]}
        hideHandle
        containerStyle={styles.container}
        ref={composeRefs(ref, taxesBottomSheetRef)}
      >
        {' '}
        <ScreenHeader
          title={t('taxes')}
          onClose={() => taxesBottomSheetRef.current?.close()}
          paddingHorizontal={theme.spacing[5]}
          paddingVertical={theme.spacing[6]}
        />
        <VStack style={styles.contentContainer} space={theme.spacing[8]}>
          <VStack style={styles.content} space={theme.spacing[4]}>
            <HStack style={styles.row}>
              <Text variant="mStrong">{t('taxesLabel')}</Text>
              <Text variant="mStrong" style={styles.amount}>
                {formatPrice((taxesAmount || 0) / 100, {
                  locale: i18n.language,
                  currency,
                })}
              </Text>
            </HStack>

            {feeApplies && (
              <>
                <Box style={styles.divider} />

                <VStack space={theme.spacing[2]}>
                  <HStack style={styles.row}>
                    <Text variant="mStrong">{t('feesLabel')}</Text>
                    <Text variant="mStrong" style={styles.amount}>
                      {formatPrice(feesAmount / 100, {
                        locale: i18n.language,
                        currency,
                      })}
                    </Text>
                  </HStack>

                  <Text
                    variant="s"
                    color="textSecondary"
                    style={styles.description}
                  >
                    {getFeeDescription()}
                  </Text>
                </VStack>
              </>
            )}

            {isWaitlistMode && waitlistFeeAmount > 0 && (
              <>
                <Box style={styles.divider} />

                <VStack space={theme.spacing[2]}>
                  <HStack style={styles.row}>
                    <Text variant="mStrong">{t('waitlistFeeLabel')}</Text>
                    <Text variant="mStrong" style={styles.amount}>
                      {formatPrice(waitlistFeeAmount / 100, {
                        locale: i18n.language,
                        currency,
                      })}
                    </Text>
                  </HStack>

                  <Text
                    variant="s"
                    color="textSecondary"
                    style={styles.description}
                  >
                    {t('waitlistFeeDescription')}
                  </Text>
                </VStack>
              </>
            )}
          </VStack>
        </VStack>
      </BottomSheet>
    );
  },
);

ConfirmBookingTaxesAndFeesBottomSheet.displayName =
  'ConfirmBookingTaxesAndFeesBottomSheet';
export { ConfirmBookingTaxesAndFeesBottomSheet };
